import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Link,
  Collapse,
} from '@mui/material';
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import type {
  CallRecord,
  CallManagementSummary,
  CallOperationalSummary,
  CallUrgency,
  CallFacts,
  CallTexts,
  CallReview,
  CallType,
  CallOutcome,
} from '@techgitter/contracts';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import { CARD_SX } from '../styles/designSystem';
import { fetchCallDetail } from '../services/callsApi';
import './CallDetailPage.css';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return value;
  }
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return value;
  }
};

const formatDuration = (secs: number | null): string => {
  if (secs === null || secs === undefined) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const formatNumber = (value: string | null): string => {
  if (!value) return '—';
  const au = value.match(/^\+61(\d{9})$/);
  if (!au) return value;
  const d = au[1];
  return d.startsWith('4')
    ? `0${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
    : `0${d.slice(0, 1)} ${d.slice(1, 5)} ${d.slice(5)}`;
};

const urgencyTone = (urgency: CallUrgency | null): ChipTone => {
  if (urgency === 'emergency') return 'error';
  if (urgency === 'high') return 'warning';
  return 'neutral';
};

const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** `existing_job` -> `Existing job`. Covers facts.details keys too. */
const humanise = (s: string): string =>
  titleCase(s.replace(/_/g, ' '));

const CALL_TYPE_LABELS: Record<CallType, string> = {
  general_enquiry: 'General enquiry',
  quote_request: 'Quote request',
  onsite_booking: 'Onsite booking',
  callback_return: 'Returned callback',
  prospective_business: 'Prospective business',
  existing_job: 'Existing job',
  supplier_or_sales: 'Supplier or sales',
  other: 'Other',
};

const OUTCOME_LABELS: Record<CallOutcome, string> = {
  resolved: 'Resolved on the call',
  message_taken: 'Message taken — someone here must act',
  message_left: 'Message left — awaiting the customer',
  transferred: 'Transferred to someone here',
  no_action: 'No action needed',
};

const OUTCOME_TONES: Record<CallOutcome, ChipTone> = {
  resolved: 'success',
  message_taken: 'warning',
  message_left: 'info',
  transferred: 'neutral',
  no_action: 'neutral',
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="call-detail__fact">
    <div className="call-detail__fact-label">{label}</div>
    <div className="call-detail__fact-value">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: string | null | undefined }> = ({
  label,
  value,
}) => (
  <div>
    <div className="call-detail__field-label">{label}</div>
    <div className="call-detail__field-value">{value || '—'}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode; badge?: React.ReactNode }> = ({
  children,
  badge,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <span className="call-detail__section-title">{children}</span>
    {badge}
  </Box>
);

/**
 * v3 `texts` — the written-up call. The work memo and todo are second person,
 * addressed to the staff member who took the call, so they are labelled as
 * such rather than presented as neutral narration to whoever is reading.
 */
const ActionsCard: React.FC<{ texts: CallTexts; staffName: string | null }> = ({
  texts,
  staffName,
}) => {
  const hasTodo = texts.todo && texts.todo.length > 0;
  if (!texts.work_memo && !hasTodo && !texts.handover_message) return null;
  return (
    <Box sx={{ ...CARD_SX, p: 2 }}>
      <SectionTitle>What happens next</SectionTitle>
      {texts.handover_message && (
        <Box sx={{ mb: texts.work_memo || hasTodo ? 2 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <div className="call-detail__field-label" style={{ margin: 0 }}>
              Handover
            </div>
            <Chip tone="warning">Someone else must act</Chip>
          </Box>
          <div className="call-detail__field-value">{texts.handover_message}</div>
        </Box>
      )}
      {texts.work_memo && (
        <Box sx={{ mb: hasTodo ? 2 : 0 }}>
          <div className="call-detail__field-label">
            {staffName ? `Memo for ${staffName}` : 'Memo for the staff member on the call'}
          </div>
          <div className="call-detail__field-value">{texts.work_memo}</div>
        </Box>
      )}
      {hasTodo && (
        <Box>
          <div className="call-detail__field-label">To do</div>
          <ul className="call-detail__list">
            {texts.todo.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};

/** v3 `facts` — the structured pass everything else is lifted from. All-staff. */
const FactsCard: React.FC<{ facts: CallFacts }> = ({ facts }) => {
  const details = Object.entries(facts.details || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );
  return (
    <Box sx={{ ...CARD_SX, p: 2 }}>
      <SectionTitle
        badge={
          <>
            {facts.call_type && (
              <Chip tone={facts.call_type === 'prospective_business' ? 'info' : 'neutral'}>
                {facts.call_type === 'other' && facts.call_type_other
                  ? facts.call_type_other
                  : CALL_TYPE_LABELS[facts.call_type] ?? humanise(facts.call_type)}
              </Chip>
            )}
            {facts.outcome && (
              <Chip tone={OUTCOME_TONES[facts.outcome] ?? 'neutral'}>
                {OUTCOME_LABELS[facts.outcome] ?? humanise(facts.outcome)}
              </Chip>
            )}
          </>
        }
      >
        Call facts
      </SectionTitle>
      <div className="call-detail__grid">
        <Field label="Caller" value={facts.caller_name} />
        <Field label="Company" value={facts.company} />
        <Field label="Callback number (as spoken)" value={facts.callback_number} />
        <Field label="Asked for" value={facts.for_whom} />
        <Field label="Device" value={facts.device} />
        <Field label="Reported symptom" value={facts.reported_symptom} />
        <Field label="Requested action" value={facts.requested_action} />
        <Field label="Commitment we made" value={facts.commitment_made} />
        <Field label="Next step" value={facts.next_step} />
        <Field label="Relayed before transfer" value={facts.relayed_before_transfer} />
      </div>
      {facts.outstanding_actions?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <div className="call-detail__field-label">We still owe them</div>
          <ul className="call-detail__list">
            {facts.outstanding_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Box>
      )}
      {facts.customer_actions?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <div className="call-detail__field-label">They agreed to</div>
          <ul className="call-detail__list">
            {facts.customer_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Box>
      )}
      {details.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {/* Shape varies by call_type and is owned by the rig's prompt
              contract, so it is rendered generically rather than pinned to
              keys that would go stale on the next prompt change. */}
          <div className="call-detail__field-label">
            {humanise(facts.call_type || 'other')} details
          </div>
          <div className="call-detail__grid" style={{ marginTop: 4 }}>
            {details.map(([k, v]) => (
              <Field
                key={k}
                label={humanise(k)}
                value={typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
              />
            ))}
          </div>
        </Box>
      )}
      {facts.unclear_fields?.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <div className="call-detail__field-label">Unclear in the recording</div>
          <div className="call-detail__field-value">
            {facts.unclear_fields.map(humanise).join(', ')}
          </div>
        </Box>
      )}
    </Box>
  );
};

/** v3 `review` — admin tier, and cleared at the retention date. */
const ReviewCard: React.FC<{ review: CallReview | null; expired: boolean }> = ({
  review,
  expired,
}) => (
  <Box sx={{ ...CARD_SX, p: 2 }}>
    <SectionTitle badge={<Chip tone="info">Admin / Manager</Chip>}>
      Management review
    </SectionTitle>
    {expired || !review ? (
      <Typography sx={{ fontSize: 13, color: '#888' }}>
        Retention reached — the management review for this call has been cleared.
      </Typography>
    ) : (
      <>
        <div className="call-detail__field-value" style={{ marginBottom: 12 }}>
          {review.summary}
        </div>
        <div className="call-detail__grid">
          <Field label="Call quality" value={review.call_quality} />
          <Field label="Customer sentiment" value={review.customer_sentiment} />
          <Field label="Pricing communication" value={review.pricing_communication} />
          <Field label="Terms relayed" value={review.terms_relayed} />
          <Field label="Handling notes" value={review.handling_notes} />
        </div>
        {review.worth_review && (
          <Box sx={{ mt: 1.5 }}>
            <div className="call-detail__field-label">Flagged for review</div>
            {review.worth_review_reasons?.length > 0 ? (
              <ul className="call-detail__list">
                {review.worth_review_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            ) : (
              <div className="call-detail__field-value">No reason recorded.</div>
            )}
          </Box>
        )}
      </>
    )}
  </Box>
);

const OperationalSummaryCard: React.FC<{
  summary: CallOperationalSummary | null;
  fallbackText: string | null;
}> = ({ summary, fallbackText }) => (
  <Box sx={{ ...CARD_SX, p: 2 }}>
    <SectionTitle>Operational summary</SectionTitle>
    {!summary && !fallbackText && (
      <Typography sx={{ fontSize: 13, color: '#888' }}>
        No operational summary on this call.
      </Typography>
    )}
    {!summary && fallbackText && (
      <div className="call-detail__field-value">{fallbackText}</div>
    )}
    {summary && (
      <>
        <div className="call-detail__field-value" style={{ marginBottom: 12 }}>
          {summary.summary || fallbackText || '—'}
        </div>
        <div className="call-detail__grid">
          <Field label="Device" value={summary.device} />
          <Field label="Reported symptom" value={summary.reported_symptom} />
          <Field label="Requested action" value={summary.requested_action} />
          <Field label="Next step" value={summary.next_step} />
          <Field label="Commitment made" value={summary.commitment_made} />
          <div>
            <div className="call-detail__field-label">Callback required</div>
            <div className="call-detail__field-value">
              {summary.callback_required ? 'Yes' : 'No'}
            </div>
          </div>
        </div>
        {summary.outstanding_actions?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <div className="call-detail__field-label">Outstanding actions</div>
            <ul className="call-detail__list">
              {summary.outstanding_actions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </Box>
        )}
        {summary.unclear_fields?.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <div className="call-detail__field-label">
              Unclear in the recording
            </div>
            <div className="call-detail__field-value">
              {summary.unclear_fields.join(', ')}
            </div>
          </Box>
        )}
      </>
    )}
  </Box>
);

const ManagementSummaryCard: React.FC<{
  summary: CallManagementSummary | null;
  fallbackText: string | null | undefined;
  expired: boolean;
}> = ({ summary, fallbackText, expired }) => (
  <Box sx={{ ...CARD_SX, p: 2 }}>
    <SectionTitle badge={<Chip tone="info">Admin / Manager</Chip>}>
      Management summary
    </SectionTitle>
    {expired && (
      <Typography sx={{ fontSize: 13, color: '#888' }}>
        Retention reached — the management summary for this call has been
        cleared.
      </Typography>
    )}
    {!expired && !summary && (
      <div className="call-detail__field-value">{fallbackText || '—'}</div>
    )}
    {!expired && summary && (
      <>
        <div className="call-detail__field-value" style={{ marginBottom: 12 }}>
          {summary.summary || fallbackText || '—'}
        </div>
        <div className="call-detail__grid">
          <Field label="Work summary" value={summary.work_summary} />
          <Field label="Call quality" value={summary.call_quality} />
          <Field label="Customer sentiment" value={summary.customer_sentiment} />
          <Field
            label="Pricing communication"
            value={summary.pricing_communication}
          />
          <Field label="Terms relayed" value={summary.terms_relayed} />
          <Field label="Handling notes" value={summary.handling_notes} />
        </div>
        {summary.worth_review && (
          <Box sx={{ mt: 1.5 }}>
            <div className="call-detail__field-label">Flagged for review</div>
            {summary.worth_review_reasons?.length > 0 ? (
              <ul className="call-detail__list">
                {summary.worth_review_reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            ) : (
              <div className="call-detail__field-value">
                No reason recorded.
              </div>
            )}
          </Box>
        )}
      </>
    )}
  </Box>
);

const TranscriptCard: React.FC<{
  transcript: string | null | undefined;
  asrModel: string | null | undefined;
}> = ({ transcript, asrModel }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ ...CARD_SX, p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ color: '#bbb', display: 'inline-flex' }}>
          {open ? (
            <IconChevronDown size={14} stroke={1.5} />
          ) : (
            <IconChevronRight size={14} stroke={1.5} />
          )}
        </span>
        <span className="call-detail__section-title">Transcript</span>
        <Chip tone="info">Admin / Manager</Chip>
        {asrModel && <Chip tone="neutral">{asrModel}</Chip>}
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: 1.5 }}>
          {transcript ? (
            <pre className="call-detail__transcript">{transcript}</pre>
          ) : (
            <Typography sx={{ fontSize: 13, color: '#888' }}>
              Retention reached — the transcript for this call has been cleared.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export const CallDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const callId = location.pathname.match(/^\/call\/(.+)$/)?.[1] ?? '';

  const [call, setCall] = useState<CallRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!callId) return;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    fetchCallDetail(callId, ctrl.signal)
      .then(setCall)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [callId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          pt: 8,
        }}
      >
        <CircularProgress />
        <Typography sx={{ fontSize: 14, color: '#888' }}>
          Getting call…
        </Typography>
      </Box>
    );
  }

  if (error || !call) {
    return (
      <Box className="call-detail">
        <Box className="call-detail__inner">
          <button
            type="button"
            className="call-detail__back"
            onClick={() => navigate(-1)}
          >
            <IconArrowLeft size={16} stroke={1.5} />
            Back
          </button>
          <Alert severity="error">{error || 'Call record not found'}</Alert>
        </Box>
      </Box>
    );
  }

  // The API OMITS the admin-tier keys for non-admin callers, so presence is
  // the gate — never a locally-computed role check. A record past its
  // retention date keeps the keys but has the memos nulled, which is why
  // "present but empty" is rendered as an explicit retention notice rather
  // than an empty panel.
  const canSeeManagement = 'managementSummary' in call;
  const canSeeTranscript = 'transcript' in call;
  const managementExpired =
    canSeeManagement && !call.managementSummary && !call.managementSummaryText;

  // v3 vs v2. Every row ingested before the three-pass switch has facts/texts/
  // review null and only the v2 objects, so the page renders whichever it has
  // rather than showing an empty v3 shell.
  const hasV3 = call.facts !== null || call.texts !== null;
  const reviewExpired = canSeeManagement && !call.review && managementExpired;
  // A cdr-origin row has no recording at all, so no transcript and no summaries.
  const noRecording = call.origin === 'cdr';

  return (
    <Box className="call-detail">
      <Box className="call-detail__inner">
        <button
          type="button"
          className="call-detail__back"
          onClick={() => navigate(-1)}
        >
          <IconArrowLeft size={16} stroke={1.5} />
          Back
        </button>

        {/* Header card */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 20, color: '#1a1a1a' }}
            >
              {call.customerName || formatNumber(call.number)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#666' }}>
              {formatDateTime(call.startTime)}
            </Typography>
            {call.urgency && (
              <Chip tone={urgencyTone(call.urgency)}>
                {titleCase(call.urgency)}
              </Chip>
            )}
            {call.reviewStatus === 'open' && <Chip tone="warning">Review</Chip>}
            {call.callbackRequired && <Chip tone="info">Callback required</Chip>}
            {call.credentialReview && (
              <Chip tone="error">Credential mentioned</Chip>
            )}
          </Box>
        </Box>

        <div className="call-detail__columns">
          {/* Metadata rail */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <SectionTitle>Call</SectionTitle>
            <div className="call-detail__facts">
              <Fact label="Time">{formatDateTime(call.startTime)}</Fact>
              <Fact label="Duration">{formatDuration(call.durationSecs)}</Fact>
              <Fact label="Direction">
                {titleCase(call.direction || 'unknown')}
                {call.callKind ? ` · ${titleCase(call.callKind)}` : ''}
              </Fact>
              <Fact label="Number">{formatNumber(call.number)}</Fact>
              <Fact label="Customer">
                {call.customerId ? (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/customer/${call.customerId}`)}
                  >
                    {call.customerName || 'Open customer'}
                  </Link>
                ) : (
                  <Chip
                    tone={call.matchStatus === 'ambiguous' ? 'warning' : 'neutral'}
                  >
                    {titleCase(call.matchStatus)}
                  </Chip>
                )}
                {call.matchStatus === 'manual' && (
                  <Box component="span" sx={{ ml: 0.5 }}>
                    <Chip tone="info">Manual</Chip>
                  </Box>
                )}
              </Fact>
              <Fact label="Job">
                {call.jobNumber ? (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/ticket/${call.jobNumber}`)}
                  >
                    #{call.jobNumber}
                  </Link>
                ) : (
                  '—'
                )}
              </Fact>
              <Fact label="Staff">
                {call.userFullName || 'Unattributed'}
                {call.extension ? ` · ext ${call.extension}` : ''}
              </Fact>
              {call.didName || call.did ? (
                <Fact label="Line called">
                  {call.didName ? humanise(call.didName) : ''}
                  {call.didName && call.did ? ' · ' : ''}
                  {call.did ? formatNumber(call.did) : ''}
                </Fact>
              ) : null}
              {call.cdr && (
                <>
                  <Fact label="Answered at">
                    {call.cdr.answeredAt ? formatDateTime(call.cdr.answeredAt) : 'Never answered'}
                  </Fact>
                  {call.cdr.finalType && (
                    <Fact label="Ended at">{humanise(call.cdr.finalType)}</Fact>
                  )}
                  {call.cdr.reasonTerminated && (
                    <Fact label="Termination">{humanise(call.cdr.reasonTerminated)}</Fact>
                  )}
                </>
              )}
              <Fact label="Source">
                {call.origin === 'cdr'
                  ? 'Phone system record (no recording)'
                  : call.origin === 'voicemail'
                  ? 'Voicemail message'
                  : 'Call recording'}
              </Fact>
              {call.transferredFrom && (
                <Fact label="Transferred from">
                  <span style={{ fontSize: 11, color: '#666', wordBreak: 'break-all' }}>
                    {call.transferredFrom}
                  </span>
                </Fact>
              )}
              <Fact label="Category">
                {call.category
                  ? `${call.category}${
                      call.businessSubcategory
                        ? ` · ${call.businessSubcategory}`
                        : ''
                    }`
                  : '—'}
              </Fact>
              <Fact label="Redactions">
                {call.redactionCards + call.redactionCredentials === 0
                  ? 'None'
                  : `${call.redactionCards} card${
                      call.redactionCards === 1 ? '' : 's'
                    }, ${call.redactionCredentials} credential${
                      call.redactionCredentials === 1 ? '' : 's'
                    }`}
              </Fact>
              <Fact label="Ingested">{formatDateTime(call.ingestedAt)}</Fact>
              {call.lastIngestedAt &&
                call.lastIngestedAt !== call.ingestedAt && (
                  <Fact label="Last updated">
                    {formatDateTime(call.lastIngestedAt)}
                  </Fact>
                )}
              <Fact label="Pipeline">
                {call.llmModel || '—'}
                {call.contextVersion ? ` · ${call.contextVersion}` : ''}
              </Fact>
              {/* The rig's id for this call — quote it when asking for a re-run. */}
              {call.sourceWorkUnitId != null && (
                <Fact label="Work unit">
                  <span
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    title={call.sourceFilename ?? undefined}
                  >
                    {call.sourceWorkUnitId}
                  </span>
                </Fact>
              )}
              {'retainUntil' in call && (
                <Fact label="Retain until">{formatDate(call.retainUntil)}</Fact>
              )}
            </div>
          </Box>

          {/* Content stack */}
          <div className="call-detail__stack">
            {noRecording && (
              <Box sx={{ ...CARD_SX, p: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#888' }}>
                  No recording for this call — it is a CDR record only, so there is
                  no transcript or summary. The call metadata on the left is what
                  the phone system reported.
                </Typography>
              </Box>
            )}
            {call.texts && (
              <ActionsCard texts={call.texts} staffName={call.userFullName} />
            )}
            {hasV3 ? (
              <>
                {call.texts?.operational_summary && (
                  <Box sx={{ ...CARD_SX, p: 2 }}>
                    <SectionTitle>Summary</SectionTitle>
                    <div className="call-detail__field-value">
                      {call.texts.operational_summary}
                    </div>
                  </Box>
                )}
                {call.facts && <FactsCard facts={call.facts} />}
              </>
            ) : (
              !noRecording && (
                <OperationalSummaryCard
                  summary={call.operationalSummary}
                  fallbackText={call.operationalSummaryText}
                />
              )
            )}
            {canSeeManagement && hasV3 && (
              <ReviewCard review={call.review ?? null} expired={reviewExpired} />
            )}
            {canSeeManagement && !hasV3 && !noRecording && (
              <ManagementSummaryCard
                summary={call.managementSummary ?? null}
                fallbackText={call.managementSummaryText}
                expired={managementExpired}
              />
            )}
            {canSeeTranscript && !noRecording && (
              <TranscriptCard
                transcript={call.transcript}
                asrModel={call.transcriptAsrModel}
              />
            )}
            {!canSeeManagement && (
              <Box sx={{ ...CARD_SX, p: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#888' }}>
                  The management summary and transcript are visible to Admin and
                  Manager roles only.
                </Typography>
              </Box>
            )}
          </div>
        </div>
      </Box>
    </Box>
  );
};
